import { __rest, __assign } from 'tslib';
import React, { useRef, useState, useCallback, Fragment } from 'react';
import mergeRefs from 'react-merge-refs';
import cn from 'classnames';
import { getDataTestId } from '@alfalab/core-components-shared/esm';
import { StatusBadge } from '@alfalab/core-components-status-badge/esm';
import { useFocus } from '@alfalab/hooks';
import { ClearButton } from '../clear-button/Component.js';

var defaultColors = {"input":"input__input_87z33","hasInnerLabel":"input__hasInnerLabel_87z33","disableUserInput":"input__disableUserInput_87z33","error":"input__error_87z33"};
require('./default.css')

var styles = {"input":"input__input_3acr0","hasInnerLabel":"input__hasInnerLabel_3acr0","size-48":"input__size-48_3acr0","size-56":"input__size-56_3acr0","size-64":"input__size-64_3acr0","size-72":"input__size-72_3acr0","block":"input__block_3acr0","errorIcon":"input__errorIcon_3acr0","successIcon":"input__successIcon_3acr0","focusVisible":"input__focusVisible_3acr0","onautofillstart":"input__onautofillstart_3acr0","onautofillcancel":"input__onautofillcancel_3acr0"};
require('./index.css')

var invertedColors = {"input":"input__input_1yab4","hasInnerLabel":"input__hasInnerLabel_1yab4","disableUserInput":"input__disableUserInput_1yab4","error":"input__error_1yab4"};
require('./inverted.css')

var colorCommonStyles = {
    default: defaultColors,
    inverted: invertedColors,
};
var SIZE_TO_CLASSNAME_MAP = {
    s: 'size-48',
    m: 'size-56',
    l: 'size-64',
    xl: 'size-72',
    48: 'size-48',
    56: 'size-56',
    64: 'size-64',
    72: 'size-72',
};
var BaseInput = React.forwardRef(function (_a, ref) {
    var _b, _c;
    var _d = _a.size, size = _d === void 0 ? 48 : _d, _e = _a.type, type = _e === void 0 ? 'text' : _e, _f = _a.block, block = _f === void 0 ? false : _f, _g = _a.colors, colors = _g === void 0 ? 'default' : _g, bottomAddons = _a.bottomAddons, dataTestId = _a.dataTestId, _h = _a.clear, clear = _h === void 0 ? false : _h, disabled = _a.disabled, error = _a.error, success = _a.success, hint = _a.hint, className = _a.className, fieldClassName = _a.fieldClassName, inputClassName = _a.inputClassName, labelClassName = _a.labelClassName, addonsClassName = _a.addonsClassName, rightAddonsProps = _a.rightAddonsProps, leftAddonsProps = _a.leftAddonsProps, focusedClassName = _a.focusedClassName, filledClassName = _a.filledClassName, label = _a.label, _j = _a.labelView, labelView = _j === void 0 ? 'inner' : _j, leftAddons = _a.leftAddons, onFocus = _a.onFocus, onBlur = _a.onBlur, onChange = _a.onChange, onClear = _a.onClear, onClick = _a.onClick, onMouseDown = _a.onMouseDown, onMouseUp = _a.onMouseUp, onAnimationStart = _a.onAnimationStart, rightAddons = _a.rightAddons, value = _a.value, defaultValue = _a.defaultValue, wrapperRef = _a.wrapperRef, readOnlyProp = _a.readOnly, FormControlComponent = _a.FormControlComponent, disableUserInput = _a.disableUserInput, restProps = __rest(_a, ["size", "type", "block", "colors", "bottomAddons", "dataTestId", "clear", "disabled", "error", "success", "hint", "className", "fieldClassName", "inputClassName", "labelClassName", "addonsClassName", "rightAddonsProps", "leftAddonsProps", "focusedClassName", "filledClassName", "label", "labelView", "leftAddons", "onFocus", "onBlur", "onChange", "onClear", "onClick", "onMouseDown", "onMouseUp", "onAnimationStart", "rightAddons", "value", "defaultValue", "wrapperRef", "readOnly", "FormControlComponent", "disableUserInput"]);
    var uncontrolled = value === undefined;
    var readOnly = readOnlyProp || disableUserInput;
    var inputRef = useRef(null);
    var focusVisible = useFocus(inputRef, 'keyboard')[0];
    var _k = useState(restProps.autoFocus), focused = _k[0], setFocused = _k[1];
    var _l = useState(defaultValue || ''), stateValue = _l[0], setStateValue = _l[1];
    var filled = Boolean(uncontrolled ? stateValue : value);
    var _m = useState(false), autofilled = _m[0], setAutofilled = _m[1];
    // отображаем крестик только для заполненного и активного инпута
    var clearButtonVisible = clear && filled && !disabled && !readOnly;
    var hasInnerLabel = label && labelView === 'inner';
    var handleInputFocus = useCallback(function (event) {
        if (!readOnly) {
            setFocused(true);
        }
        if (onFocus) {
            onFocus(event);
        }
    }, [onFocus, readOnly]);
    var handleInputBlur = useCallback(function (event) {
        setFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    }, [onBlur]);
    var handleInputChange = useCallback(function (event) {
        if (onChange) {
            onChange(event, { value: event.target.value });
        }
        if (uncontrolled) {
            setStateValue(event.target.value);
        }
    }, [onChange, uncontrolled]);
    var handleClear = useCallback(function (event) {
        if (!clearButtonVisible)
            return;
        if (uncontrolled) {
            setStateValue('');
        }
        if (onClear) {
            onClear(event);
        }
        if (inputRef.current && !focused) {
            inputRef.current.focus();
        }
    }, [clearButtonVisible, focused, onClear, uncontrolled]);
    var handleAnimationStart = useCallback(function (event) {
        if (onAnimationStart) {
            onAnimationStart(event);
        }
        setAutofilled(event.animationName.includes('start'));
    }, [onAnimationStart]);
    var renderRightAddons = function () {
        var addonsVisible = clearButtonVisible || rightAddons || error || success;
        return (addonsVisible && (React.createElement(Fragment, null,
            clearButtonVisible && (React.createElement(ClearButton, { onClick: handleClear, disabled: disabled, colors: colors, dataTestId: getDataTestId(dataTestId, 'clear-icon') })),
            rightAddons,
            error && (React.createElement("div", { className: styles.errorIcon, "data-addon": 'error-icon' },
                React.createElement(StatusBadge, { view: 'negative-alert', size: 20, dataTestId: getDataTestId(dataTestId, 'error-icon') }))),
            success && !error && (React.createElement("div", { className: styles.successIcon },
                React.createElement(StatusBadge, { view: 'positive-checkmark', size: 20, dataTestId: getDataTestId(dataTestId, 'success-icon') }))))));
    };
    return FormControlComponent ? (React.createElement(FormControlComponent, { ref: wrapperRef, className: cn(className, focused && focusedClassName, filled && filledClassName), fieldClassName: cn(fieldClassName, (_b = {},
            _b[styles.focusVisible] = focusVisible,
            _b)), labelClassName: labelClassName, addonsClassName: addonsClassName, size: size, colors: colors, block: block, disabled: disabled, readOnly: readOnlyProp, filled: filled || autofilled || focused, focused: focused, error: error, label: label, labelView: labelView, hint: hint, leftAddons: leftAddons, rightAddons: renderRightAddons(), bottomAddons: bottomAddons, onClick: onClick, onMouseDown: onMouseDown, onMouseUp: onMouseUp, dataTestId: getDataTestId(dataTestId, 'form-control'), rightAddonsProps: rightAddonsProps, leftAddonsProps: leftAddonsProps },
        React.createElement("input", __assign({}, restProps, { className: cn(styles.input, colorCommonStyles[colors].input, (_c = {},
                _c[colorCommonStyles[colors].disableUserInput] = disableUserInput,
                _c[colorCommonStyles[colors].error] = error,
                _c[styles[SIZE_TO_CLASSNAME_MAP[size]]] = hasInnerLabel,
                _c[styles.hasInnerLabel] = hasInnerLabel,
                _c[colorCommonStyles[colors].hasInnerLabel] = hasInnerLabel,
                _c), inputClassName), disabled: disabled, onBlur: handleInputBlur, onFocus: handleInputFocus, onChange: handleInputChange, onAnimationStart: handleAnimationStart, ref: mergeRefs([ref, inputRef]), type: type, value: uncontrolled ? stateValue : value, readOnly: readOnly, "data-test-id": dataTestId, "aria-label": typeof label === 'string' ? label : undefined })))) : null;
});

export { BaseInput };
